import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatMoney = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const f = parseFloat(value);
    if (isNaN(f)) return 'N/A';
    return `R$ ${f.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
};

function DashboardVendasDia() {
    const hoje = new Date();
    const [mes, setMes] = useState(hoje.getMonth() + 1);
    const [ano, setAno] = useState(hoje.getFullYear());

    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { authenticatedFetch, logout } = useAuth();

    const anosDisponiveis = Array.from({ length: 6 }, (_, i) => hoje.getFullYear() - i);

    const fetchData = async (month, year) => {
        setLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/api/dashboard-vendas-dia/?month=${month}&year=${year}`;
            const response = await authenticatedFetch(url);

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    throw new Error('Sessão expirada ou não autorizada.');
                }
                const errorText = await response.text();
                throw new Error(`Erro ${response.status}: ${errorText.slice(0, 100)}`);
            }

            const data = await response.json();
            setDados(data);
        } catch (err) {
            setError(`Falha ao carregar dados: ${err.message}`);
            setDados(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(mes, ano);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mes, ano]);

    return (
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5" fontWeight="bold">
                Dashboard de Vendas por Dia
            </Typography>

            <Paper elevation={3} sx={{ p: 3, display: 'flex', gap: 2, width: 'fit-content' }}>
                <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel id="mes-label">Mês</InputLabel>
                    <Select
                        labelId="mes-label"
                        label="Mês"
                        value={mes}
                        onChange={(e) => setMes(e.target.value)}
                    >
                        {MESES.map((nome, idx) => (
                            <MenuItem key={idx + 1} value={idx + 1}>{nome}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel id="ano-label">Ano</InputLabel>
                    <Select
                        labelId="ano-label"
                        label="Ano"
                        value={ano}
                        onChange={(e) => setAno(e.target.value)}
                    >
                        {anosDisponiveis.map((a) => (
                            <MenuItem key={a} value={a}>{a}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography>Carregando dados...</Typography>
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && dados && (
                <Paper elevation={3} sx={{ p: 2, overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dia</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dia da Semana</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Vendas Brutas</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Descontos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Vendas Líquidas</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dados.dias.map((dia) => (
                                    <TableRow key={dia.date} hover>
                                        <TableCell>{dia.day.toString().padStart(2, '0')}</TableCell>
                                        <TableCell>{dia.day_of_week}</TableCell>
                                        <TableCell align="right">{formatMoney(dia.vendas_brutas)}</TableCell>
                                        <TableCell align="right">{formatMoney(dia.descontos)}</TableCell>
                                        <TableCell align="right">{formatMoney(dia.vendas_liquidas)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>

                            <TableBody>
                                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total do Mês</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatMoney(dados.totais.vendas_brutas)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatMoney(dados.totais.descontos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatMoney(dados.totais.vendas_liquidas)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {!loading && !error && dados && dados.dias.length === 0 && (
                <Alert severity="info">Nenhum dado encontrado para o período selecionado.</Alert>
            )}
        </Box>
    );
}

export default DashboardVendasDia;